import { useEffect, useState } from "react";
import { Download, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";

/**
 * Introducer's monthly commission statements list.
 *
 * Each statement is admin-published. Introducer can browse the history and
 * download the countersigned PDF (opens inline in a new tab).
 */
export default function IntroducerStatementsPage() {
  const { t, locale } = useI18n();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const BACKEND_URL =
    typeof window !== "undefined" && window.location && window.location.origin
      ? window.location.origin
      : (process.env.REACT_APP_BACKEND_URL || "");

  useEffect(() => {
    setLoading(true);
    api
      .get("/introducer/statements")
      .then(({ data }) => setRows(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const fmtEur = (n) =>
    new Intl.NumberFormat(
      locale === "en" ? "en-GB" : locale === "de" ? "de-DE" : locale === "no" ? "nb-NO" : "it-IT",
      { style: "currency", currency: "EUR", maximumFractionDigits: 2 }
    ).format(Number(n || 0));

  return (
    <PortalLayout>
      <section
        className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8"
        data-testid="introducer-statements-page"
      >
        <Link
          to="/introducer"
          className="inline-flex items-center gap-2 br-overline mb-6 hover:text-[color:var(--br-parchment)] transition-colors"
          data-testid="stmt-back"
        >
          <ArrowLeft size={14} /> {t("intro.contract_back")}
        </Link>

        <div className="br-fade-up">
          <div className="br-overline">{t("intro.stmt_overline")}</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            {t("intro.stmt_heading_a")}{" "}
            <em>{t("intro.stmt_heading_em")}</em>
          </h1>
          <p className="text-[1.05rem] text-[color:var(--br-text)] mt-3 max-w-[720px]">
            {t("intro.stmt_intro")}
          </p>
        </div>

        {error && (
          <div className="mt-6 br-card p-4 text-[color:var(--br-red)]" data-testid="stmt-error">
            {error}
          </div>
        )}

        <div className="mt-10 br-card overflow-x-auto br-fade-up br-delay-1" data-testid="stmt-table-wrap">
          <table className="w-full text-left border-collapse" data-testid="stmt-table">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                <Th>{t("intro.stmt_col_period")}</Th>
                <Th>{t("intro.stmt_col_ref")}</Th>
                <Th align="right">{t("intro.stmt_col_gross")}</Th>
                <Th align="right">{t("intro.stmt_col_withholding")}</Th>
                <Th align="right">{t("intro.stmt_col_net")}</Th>
                <Th align="right">{t("intro.stmt_col_download")}</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-[color:var(--br-text-dim)] font-mono-fin text-xs tracking-[0.24em] uppercase">
                    {t("intro.stmt_loading")}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-[color:var(--br-text-dim)]" data-testid="stmt-empty">
                    {t("intro.stmt_empty")}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b"
                    style={{ borderColor: "var(--br-line)" }}
                    data-testid={`stmt-row-${r.id}`}
                  >
                    <Td className="font-serif-display italic text-[1.05rem] text-[color:var(--br-parchment)]">
                      {r.period_label}
                    </Td>
                    <Td className="font-mono-fin text-[0.78rem] text-[color:var(--br-text-dim)]">
                      {r.statement_number}
                    </Td>
                    <Td align="right" className="font-mono-fin text-[color:var(--br-text-dim)]">
                      {fmtEur(r.gross_commission)}
                    </Td>
                    <Td align="right" className="font-mono-fin text-[color:var(--br-text-dim)]">
                      − {fmtEur(r.withholding_amount)}
                    </Td>
                    <Td align="right" className="font-mono-fin text-[color:var(--br-parchment)] font-semibold">
                      {fmtEur(r.net_amount)}
                    </Td>
                    <Td align="right">
                      <a
                        href={`${BACKEND_URL}/api/introducer/statements/${r.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 font-mono-fin text-[0.7rem] tracking-[0.18em] uppercase transition-colors hover:text-[color:var(--br-parchment)]"
                        style={{ color: "var(--br-bronze-soft)" }}
                        data-testid={`stmt-download-${r.id}`}
                      >
                        <Download size={14} /> {t("intro.stmt_col_download")}
                      </a>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-[0.78rem] text-[color:var(--br-text-dim)] leading-relaxed max-w-[720px]">
          {t("intro.stmt_footnote")}
        </p>
      </section>
    </PortalLayout>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className="px-5 py-4 font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase font-medium"
      style={{ color: "var(--br-text-dim)", textAlign: align }}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left", className = "", style }) {
  return (
    <td className={`px-5 py-5 text-[0.9rem] ${className}`} style={{ textAlign: align, ...style }}>
      {children}
    </td>
  );
}
